import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const STEPS = [
  {
    title: "Understand",
    lead: "Discuss your business, priorities, and challenges.",
    body: "Every engagement starts with listening. In a relaxed first conversation, we ask about your goals, what’s working, and where friction lives—marketing, people, revenue, operations, or something harder to name. You won’t be pitched; you’ll be heard.",
  },
  {
    title: "Connect",
    lead: "Identify relevant solutions and introduce the appropriate specialists.",
    body: "We map what we heard against our solution areas and introduce the specialists best positioned to help—marketing teams, wellness providers, revenue-cycle experts, shipping analysts. If nothing fits, we say so. A poor-fit introduction helps no one.",
  },
  {
    title: "Move forward",
    lead: "Clarify the next steps, responsibilities, and implementation process.",
    body: "When a solution looks right, we help you understand exactly what happens next: scope, responsibilities, timing, and cost. You stay in control of the decision, and we remain reachable as the work begins.",
  },
];

export default function Approach() {
  return (
    <>
      <Seo
        title="Our Approach — Riverstone Partners"
        description="A clear path from conversation to action: understand your priorities, connect the right specialists, and move forward with clarity."
        path="/approach"
      />

      <section className="border-b border-charcoal/10 bg-sage/50 py-16 md:py-24">
        <div className="shell max-w-3xl">
          <p className="eyebrow text-forest">Our approach</p>
          <h1 className="mt-5 font-serif text-4xl font-light leading-[1.1] tracking-tight text-navy sm:text-5xl" data-testid="approach-title">
            A clear path from conversation to action.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-charcoal/80">
            Advisory works best when it’s unhurried. Our approach is built on three
            steps that keep you informed, in control, and never pressured.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="shell">
          <div className="space-y-0">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="grid gap-6 border-b border-charcoal/10 py-10 md:grid-cols-[140px_1fr_1.4fr] md:gap-10 md:py-14">
                  <span className="font-serif text-6xl font-light text-river/70">{i + 1}</span>
                  <div>
                    <h2 className="font-serif text-2xl font-light tracking-tight text-navy sm:text-3xl">{s.title}</h2>
                    <p className="mt-3 font-medium text-forest">{s.lead}</p>
                  </div>
                  <p className="text-lg leading-relaxed text-charcoal/80 md:pt-1">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-14">
            <div className="border-l-2 border-forest bg-sage/50 p-8 md:p-10">
              <h2 className="font-serif text-2xl text-navy">What you can expect from us</h2>
              <ul className="mt-5 space-y-3 text-charcoal/80">
                <li className="flex gap-3"><span className="mt-2 h-px w-6 shrink-0 bg-river" aria-hidden="true" />Honesty about fit—even when the answer is “not yet.”</li>
                <li className="flex gap-3"><span className="mt-2 h-px w-6 shrink-0 bg-river" aria-hidden="true" />Clear separation between Riverstone’s advisory role and the providers’ delivery work.</li>
                <li className="flex gap-3"><span className="mt-2 h-px w-6 shrink-0 bg-river" aria-hidden="true" />No contact information required to explore ideas on this site.</li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="mt-14 text-center">
            <h2 className="font-serif text-3xl font-light tracking-tight text-navy">Ready to begin with a conversation?</h2>
            <Link to="/contact" className="btn-primary mt-7" data-testid="approach-cta">
              Request a Conversation <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="hidden md:block">
        <img
          src={IMAGES.meetingHands}
          alt="Colleagues working together at a table"
          width="1600"
          height="900"
          loading="lazy"
          className="h-[420px] w-full object-cover"
        />
      </section>
    </>
  );
}
